import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Light/dark via a data attribute + CSS variables. No re-render needed to
// restyle — the CSS keys off [data-theme]. Choice persists to localStorage and
// defaults from the OS preference.
type Theme = 'light' | 'dark';
// Position du logo (design-kit : [data-logo] sur <html> masque l'autre emplacement).
// 'top' = dans la barre du haut (.brand-logo), 'bottom' = au pied de la barre
// latérale (.sidebar-logo). Préférence par utilisateur, persistée comme le thème.
type LogoPosition = 'top' | 'bottom';
interface ThemeContextValue {
  theme: Theme; toggleTheme: () => void;
  logoPosition: LogoPosition; toggleLogoPosition: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialTheme(): Theme {
  const stored = localStorage.getItem('theme') as Theme | null;
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getInitialLogoPosition(): LogoPosition {
  const stored = localStorage.getItem('logo-position');
  return stored === 'top' ? 'top' : 'bottom';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>(getInitialLogoPosition);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-logo', logoPosition);
    localStorage.setItem('logo-position', logoPosition);
  }, [logoPosition]);

  return (
    <ThemeContext.Provider value={{
      theme, toggleTheme: () => setTheme(t => (t === 'light' ? 'dark' : 'light')),
      logoPosition, toggleLogoPosition: () => setLogoPosition(p => (p === 'top' ? 'bottom' : 'top')),
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
